'use client';
import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes';
import type { ThemeProviderProps } from 'next-themes/dist/types';
import { useEffect, useLayoutEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  FRAMED_DARK_SCRIPT,
  getLocalDateString,
  getTimeBasedTheme,
  THEME_DATE_KEY,
  THEME_KEY,
} from '@/lib/theme-schedule';
import { isFramed } from '@/lib/is-framed';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

function ThemeController() {
  const { setTheme } = useTheme();

  useEffect(() => {
    function applyTimeBasedTheme() {
      const storedDate = localStorage.getItem(THEME_DATE_KEY);
      const todayDate = getLocalDateString();

      // If user has a manual override for today, respect it
      if (storedDate === todayDate) {
        return;
      }

      // No override for today — clear any stale override and apply time-based theme
      if (storedDate) {
        localStorage.removeItem(THEME_DATE_KEY);
      }

      const timeBasedTheme = getTimeBasedTheme();
      const currentTheme = localStorage.getItem(THEME_KEY);
      if (currentTheme !== timeBasedTheme) {
        setTheme(timeBasedTheme);
      }
    }

    // Run immediately on mount
    applyTimeBasedTheme();

    // Check every minute: handles day changes AND 6 AM / 6 PM boundary crossings
    const interval = setInterval(applyTimeBasedTheme, 60_000);
    return () => clearInterval(interval);
  }, [setTheme]);

  return null;
}

export function ThemeProvider({ children, ...props }: Readonly<ThemeProviderProps>) {
  const pathname = usePathname();

  // Admin is light-only. `forcedTheme` isn't just a DOM tweak: next-themes reads
  // it FIRST in both its pre-paint script and the effect that syncs theme state
  // to <html> on every mount (node_modules/next-themes dist — the effect is
  // `R(forcedTheme ?? theme)`), so it wins that mount-time sync outright instead
  // of racing it. A nested <ThemeProvider forcedTheme="light"> can't do this: two
  // provider instances share one context, and the inner one is a no-op passthrough
  // once an outer one already exists — this has to be the same root instance.
  //
  // Framed (the Chrome extension's side panel): always dark. FRAMED_DARK_SCRIPT
  // paints it dark before hydration; this holds it there afterwards. A layout
  // effect, so the re-render lands before the first paint after hydration.
  const [framed, setFramed] = useState(false);
  useIsomorphicLayoutEffect(() => {
    if (isFramed()) setFramed(true);
  }, []);
  const forcedTheme = pathname?.startsWith('/admin') ? 'light' : framed ? 'dark' : props.forcedTheme;

  return (
    <NextThemesProvider
      {...props}
      enableSystem={false}
      storageKey={THEME_KEY}
      forcedTheme={forcedTheme}
    >
      {/* eslint-disable-next-line react/no-danger */}
      <script dangerouslySetInnerHTML={{ __html: FRAMED_DARK_SCRIPT }} />
      <ThemeController />
      {children}
    </NextThemesProvider>
  );
}
